import { forwardRef, useCallback, useMemo } from 'react';
import { IconButton } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import IconDelete from 'src/assets/icons/Delete';
import { IconEditPencil } from 'src/assets/icons/IconEditPencil';
import { Checkbox } from 'src/components/Checkbox';
import { ContextMenu, useToggleOpen } from 'src/components/ContextMenu';
import { CUSTOM_BUTTON_COLOR, CUSTOM_BUTTON_SIZE, CustomButton } from 'src/components/CustomButton';
import { CustomTag } from 'src/components/CustomTag';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { PersonaAvatar } from 'src/components/PersonaAvatar';
import { PersonaPainPoints } from 'src/components/PersonaPainPoints';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { getAgeGroupLabel, getGenderLabel } from 'src/data/demographics';
import { AVATAR_TYPE } from 'src/data/persona';

import { useStyles } from './useStyles';

export const CustomerPersonaListItem = forwardRef(
  (
    {
      className,
      classes,
      customerPersona,
      onEditClick,
      onDeleteClick,
      showCheckbox,
      selected,
      onToggle,
      onSaveClick,
      brandVoice,
      isSaving,
      disabled,
    },
    ref
  ) => {
    const styles = useStyles({ classes });

    const { open, handleOpen, handleClose } = useToggleOpen();

    const { name, minAge, maxAge, gender, avatarType, painPoints, isSuggested } = customerPersona;

    const handleEdit = useCallback(() => {
      onEditClick({ customerPersona, brandVoice: customerPersona.brandVoice || brandVoice });
    }, [onEditClick, customerPersona, brandVoice]);

    const contextMenuItems = useMemo(
      () => [
        {
          id: 'edit',
          label: 'Edit',
          icon: <IconEditPencil />,
          onClick: handleEdit,
        },
        {
          id: 'delete',
          label: 'Delete',
          icon: <IconDelete />,
          onClick: () => onDeleteClick(customerPersona),
        },
      ],
      [onDeleteClick, handleEdit, customerPersona]
    );

    return (
      <div
        className={cx(styles.root, className, {
          [styles.menuOpened]: open,
          [styles.selected]: selected,
          [styles.disabled]: disabled,
        })}
        data-testid="customerPersonaListItem"
        onClick={() => onToggle?.(customerPersona)}
        ref={ref}
      >
        {disabled && <div className={styles.disabledOverlay}></div>}
        <div className={styles.header}>
          <PersonaAvatar
            className={styles.avatar}
            avatarType={avatarType}
          />
          <div>
            <TruncatedTypography
              className={styles.name}
              data-testid="customerPersonaName"
              variant={TYPOGRAPHY_VARIANT.semiBold14}
            >
              {name}
            </TruncatedTypography>
            <CustomTypography
              className={styles.description}
              variant={TYPOGRAPHY_VARIANT.regular12}
            >
              {minAge} - {getAgeGroupLabel(maxAge)} <span>•</span> {getGenderLabel(gender)}
            </CustomTypography>
          </div>

          {showCheckbox ? (
            <div className={styles.actions}>
              <CustomTooltip title="Edit">
                <IconButton
                  className={styles.iconButton}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleEdit();
                  }}
                >
                  <IconEditPencil />
                </IconButton>
              </CustomTooltip>
              <CustomTooltip title="Select">
                <span>
                  <Checkbox
                    className={styles.checkbox}
                    checked={selected}
                  />
                </span>
              </CustomTooltip>
            </div>
          ) : (
            <div className={styles.actions}>
              {isSuggested && (
                <>
                  <CustomTag
                    className={styles.suggestedTag}
                    tag="Suggested"
                  />
                </>
              )}
              <ContextMenu
                className={styles.buttonMenu}
                items={contextMenuItems.filter((item) =>
                  !onDeleteClick ? item.id === 'edit' : item
                )}
                onOpen={handleOpen}
                onClose={handleClose}
                horizontal
              />
            </div>
          )}
        </div>
        <PersonaPainPoints
          className={styles.painPoints}
          painPoints={painPoints}
        />
        {isSuggested && (
          <div className={styles.saveButtonContainer}>
            <CustomTooltip title={`Save to your target audiences`}>
              <CustomButton
                size={CUSTOM_BUTTON_SIZE.small}
                color={CUSTOM_BUTTON_COLOR.silver}
                loading={isSaving}
                onClick={() => onSaveClick?.(brandVoice, customerPersona)}
              >
                Save
              </CustomButton>
            </CustomTooltip>
          </div>
        )}
      </div>
    );
  }
);

CustomerPersonaListItem.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.shape({}),
  customerPersona: PropTypes.shape({
    name: PropTypes.string,
    minAge: PropTypes.number,
    maxAge: PropTypes.number,
    gender: PropTypes.string,
    painPoints: PropTypes.arrayOf(PropTypes.string),
    avatarType: PropTypes.oneOf(Object.values(AVATAR_TYPE)),
    brandVoice: PropTypes.shape({}),
    isSuggested: PropTypes.bool,
  }).isRequired,
  onEditClick: PropTypes.func.isRequired,
  onDeleteClick: PropTypes.func,
  showCheckbox: PropTypes.bool,
  selected: PropTypes.bool,
  onToggle: PropTypes.func,
  onSaveClick: PropTypes.func,
  brandVoice: PropTypes.shape({
    name: PropTypes.string,
  }),
  isSaving: PropTypes.bool,
  disabled: PropTypes.bool,
};

CustomerPersonaListItem.defaultProps = {
  className: null,
  classes: null,
  onDeleteClick: null,
  showCheckbox: false,
  selected: false,
  onToggle: null,
  onSaveClick: null,
  brandVoice: null,
  isSaving: false,
  disabled: false,
};
